import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * La línia de temps a pantalla sencera, per projectar-la.
 *
 * **No són diapositives, i és a posta.** Els escenaris sí que en són —cadascun
 * és una història independent— però una línia de temps és una cadena: el que
 * s'hi ha d'entendre és que una fita en fa possible la següent, i partir-la en
 * pantalles independents és justament trencar això. Aquí la cadena hi és
 * sencera, de l'horitzó cap avall fins avui.
 *
 * El que sí que hi ha és **control de presentador**: les fletxes avancen d'una
 * fita a l'altra, la centren i la destaquen. Qui parla pot anar-hi pas a pas
 * sense que el públic perdi de vista d'on ve i cap a on va.
 *
 * La resta de decisions surten de projectar-ho en una aula:
 *
 * - Cos de text gran. Les descripcions de les fites es llegeixen des del fons.
 * - Una columna, i estreta (uns 62 caràcters), perquè l'ull no es perdi.
 * - Tinta sobre paper i un sol accent, el mateix de l'aplicació.
 * - El futur preferit obre, i «Avui» tanca: són els dos extrems del recorregut
 *   i han de ser visibles com a tals.
 */

const NIVELLS_PLENS = { full: 3, partial: 2, none: 0 };

const Punts = ({ plens }) => (
  <span className="inline-flex gap-1" aria-hidden="true">
    {[0, 1, 2].map(i => (
      <span key={i}
        className={`block h-2 w-2 rounded-full border border-current ${i < plens ? 'bg-current' : ''}`} />
    ))}
  </span>
);

const TimelinePresentation = ({ case_, exercise, milestones, onClose }) => {
  const { t } = useTranslation();

  const ordenades = [...milestones].sort((a, b) => (a.position - b.position) || (a.id - b.id));
  const [actiu, setActiu] = useState(0);
  const refs = useRef([]);

  const vesA = useCallback((i) => {
    if (i < 0 || i >= ordenades.length) return;
    setActiu(i);
    refs.current[i]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [ordenades.length]);

  useEffect(() => {
    const tecla = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || e.key === ' ') {
        e.preventDefault();
        setActiu(prev => {
          const seguent = Math.min(prev + 1, ordenades.length - 1);
          refs.current[seguent]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return seguent;
        });
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        setActiu(prev => {
          const anterior = Math.max(prev - 1, 0);
          refs.current[anterior]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return anterior;
        });
      } else if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', tecla);
    return () => document.removeEventListener('keydown', tecla);
  }, [ordenades.length, onClose]);

  const horitzo = exercise.horizon_label || t('backcasting.theHorizon');

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-neutral-50">
      <div className="mx-auto max-w-5xl px-8 pb-32 pt-10 sm:px-12">

        {/* Capçalera discreta: on som, no què mirar. */}
        <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-neutral-200 pb-4">
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-neutral-500">
            {case_?.name}
            <span className="text-neutral-400"> · {exercise.name}</span>
          </div>
          <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-neutral-400">
            {t('backcasting.backcasting')} · {horitzo}
          </div>
        </div>

        {/* El futur preferit obre la projecció: tot el que ve després es mesura
            contra aquest paràgraf, i convé tenir-lo a la vista abans de baixar. */}
        <div className="mt-12">
          <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-accent-700">
            {horitzo} · {t('backcasting.preferredFuture')}
          </p>
          {exercise.vision_statement && (
            <p className="mt-4 max-w-[62ch] text-[clamp(1.25rem,2.3vw,1.9rem)] font-medium leading-[1.35] tracking-[-0.015em] text-neutral-900">
              {exercise.vision_statement}
            </p>
          )}
        </div>

        {/* La cadena. Es llegeix cap avall, que és anar cap enrere en el temps. */}
        <ol className="mt-16 border-l border-neutral-200 pl-10">
          {ordenades.map((fita, i) => {
            const esActiva = i === actiu;
            const plens = NIVELLS_PLENS[fita.control_level];
            return (
              <li
                key={fita.id}
                ref={el => { refs.current[i] = el; }}
                className={`relative pb-14 transition-opacity duration-300 ${
                  esActiva ? 'opacity-100' : 'opacity-45'
                }`}
              >
                <span
                  className={`absolute -left-[3.1rem] top-3 block rounded-full transition-all ${
                    fita.is_critical
                      ? 'h-3.5 w-3.5 border-2 border-accent-700 bg-accent-700'
                      : 'h-3 w-3 border-2 border-neutral-300 bg-neutral-50'
                  } ${esActiva ? 'ring-4 ring-accent-100' : ''}`}
                  aria-hidden="true"
                />

                {fita.period_label && (
                  <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-neutral-500">
                    {fita.period_label}
                  </p>
                )}
                <h3 className="mt-2 max-w-[24ch] text-[clamp(1.4rem,2.6vw,2.15rem)] font-semibold leading-[1.15] tracking-[-0.02em] text-neutral-900">
                  {fita.title || t('backcasting.untitledMilestone')}
                </h3>
                {fita.description && (
                  <p className="mt-4 max-w-[62ch] text-[clamp(1rem,1.4vw,1.25rem)] leading-[1.65] text-neutral-700">
                    {fita.description}
                  </p>
                )}

                {(fita.is_critical || fita.control_level) && (
                  <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-[clamp(0.8rem,1vw,0.95rem)]">
                    {fita.is_critical && (
                      <span className="font-medium text-accent-700">{t('backcasting.criticalMilestone')}</span>
                    )}
                    {fita.control_level && (
                      <span className="inline-flex items-center gap-2.5 text-neutral-500">
                        <Punts plens={plens} />
                        {t(`backcasting.control.${fita.control_level}`)}
                      </span>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ol>

        {/* I «Avui» tanca: el punt d'on surt tot el que s'ha de fer. */}
        <div className="border-t border-neutral-200 pt-8">
          <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-neutral-500">
            {t('backcasting.today')}
          </p>
          <p className="mt-2 max-w-[62ch] text-[clamp(1rem,1.4vw,1.25rem)] text-neutral-500">
            {t('backcasting.todayNote')}
          </p>
        </div>
      </div>

      {/* Controls. Discrets a posta: durant la projecció no han de competir amb
          el contingut, però han de ser trobables. */}
      <div className="fixed inset-x-0 bottom-0 border-t border-neutral-200 bg-neutral-50/95 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-8 py-3 sm:px-12">
          <span className="hidden text-[11px] uppercase tracking-[0.12em] text-neutral-400 sm:block">
            ← → {t('backcasting.navigate')} · ESC {t('backcasting.close')}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => vesA(actiu - 1)}
              disabled={actiu === 0}
              aria-label={t('backcasting.moveLater')}
              className="rounded-md border border-neutral-300 px-3 py-1.5 text-sm text-neutral-700 transition-colors hover:bg-white disabled:opacity-40"
            >
              ←
            </button>
            <span className="min-w-[3.5rem] text-center font-mono text-sm text-neutral-500">
              {actiu + 1} / {ordenades.length}
            </span>
            <button
              onClick={() => vesA(actiu + 1)}
              disabled={actiu === ordenades.length - 1}
              aria-label={t('backcasting.moveEarlier')}
              className="rounded-md border border-neutral-300 px-3 py-1.5 text-sm text-neutral-700 transition-colors hover:bg-white disabled:opacity-40"
            >
              →
            </button>
            <button
              onClick={onClose}
              className="ml-3 rounded-md bg-neutral-900 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-neutral-700"
            >
              {t('backcasting.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimelinePresentation;
